import { useEffect, useState } from 'react';
import { ReadMeGame } from './ReadMeGame';
import { SameBrainGame } from './SameBrainGame';
import { DontMatchGame } from './DontMatchGame';
import { CustomQuestionGame } from './CustomQuestionGame';
import { fetchSessionSnapshot, rememberActiveSession, type SessionSnapshot } from './lib/session-core';
import type { MomentKey } from './lib/challenge-core';
import { SafetyControls } from './SafetyControls';
import { trackAnalytics } from './lib/analytics';
import { t } from './lib/i18n';

export function SessionGame({ sessionId, initData, userId, onHome, onChallengeMoment }: {
  sessionId: string; initData: string; userId: string; onHome: (createChallenge?: boolean) => void; onChallengeMoment?: (momentKey: MomentKey) => void;
}) {
  const [snapshot, setSnapshot] = useState<SessionSnapshot | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    rememberActiveSession(sessionId);
    void fetchSessionSnapshot(sessionId, initData).then((next) => {
      if (!cancelled) setSnapshot(next);
    }).catch((reason: unknown) => {
      if (!cancelled) {
        trackAnalytics('unrecoverable_session', { scope: 'session' });
        setError(reason instanceof Error ? reason.message : t('Could not restore this game.'));
      }
    });
    return () => { cancelled = true; };
  }, [sessionId, initData]);

  if (error) return <main className="app-shell quiet-screen"><div className="brand-mark">UTAGE</div><h1 className="error-title">{t('Couldn’t restore this game.')}</h1><p className="quiet-copy">{error}</p><button className="secondary-action" onClick={() => { rememberActiveSession(null); onHome(false); }}>{t('HOME')}</button></main>;
  if (!snapshot) return <main className="app-shell quiet-screen"><div className="brand-mark">UTAGE</div><p className="quiet-copy">{t('Restoring your game…')}</p><button className="text-action" type="button" onClick={() => { rememberActiveSession(null); onHome(false); }}>{t('HOME')}</button></main>;
  if (snapshot.session.status === 'expired') return <main className="app-shell quiet-screen energy-quiet"><div className="brand-mark">UTAGE</div><h1 className="error-title">{t('This game expired.')}</h1><p className="quiet-copy">{t('Your old session is closed. Start a fresh human moment from Home.')}</p><button className="secondary-action" onClick={() => { rememberActiveSession(null); onHome(false); }}>{t('HOME')}</button></main>;

  const game = snapshot.customPrompt
    ? <CustomQuestionGame initialSessionId={sessionId} initData={initData} userId={userId} initialSnapshot={snapshot} onHome={onHome} />
    : snapshot.session.interactionType === 'read_me'
      ? <ReadMeGame initialSessionId={sessionId} initData={initData} userId={userId} initialSnapshot={snapshot} onHome={onHome} />
      : snapshot.session.interactionType === 'dont_match'
        ? <DontMatchGame initialSessionId={sessionId} initData={initData} userId={userId} initialSnapshot={snapshot} onHome={onHome} />
        : <SameBrainGame initialSessionId={sessionId} initData={initData} userId={userId} initialSnapshot={snapshot} onHome={onHome} onChallengeMoment={onChallengeMoment} />;
  return <>{game}<SafetyControls initData={initData} context={{ sessionId }} targets={snapshot.players.filter((player) => player.userId !== userId).map((player) => ({ userId: player.userId, firstName: player.firstName }))} /></>;
}
